import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

type RecorderState = 'idle' | 'recording' | 'recorded' | 'uploading' | 'transcribed' | 'failed';

interface TranscriptionSegment {
  text?: string;
  speakerLabel?: string;
  startMs?: number;
  endMs?: number;
  start?: number;
  end?: number;
}

interface SavedVoiceTranscription {
  id: number;
  fileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  durationSeconds?: number;
  recordedAt?: string;
  transcriptionText?: string;
  intentName?: string;
  intentGroup?: string;
  intentConfidence?: number;
  intentNeedsReview?: boolean;
  intentRelevant?: boolean;
  intentPromotional?: boolean;
  intentComplaint?: boolean;
  intentResponse?: string;
  createdAt?: string;
  previewUrl?: string;
}

@Component({
  selector: 'app-rnd-voice-transcription',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rnd-voice-transcription.component.html',
  styleUrls: ['./rnd-voice-transcription.component.scss'],
})
export class RndVoiceTranscriptionComponent implements OnInit, OnDestroy {
  state: RecorderState = 'idle';
  elapsedSeconds = 0;
  audioUrl = '';
  mimeType = '';
  fileSize = 0;
  transcription = '';
  rawResponse = '';
  savedIntentResponse = '';
  errorMessage = '';
  showRaw = false;
  showSavedIntent = false;
  savingAnalysis = false;
  savedRecords: SavedVoiceTranscription[] = [];
  recordsLoading = false;

  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordedBlob: Blob | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadSavedRecords();
  }

  get canRecord(): boolean {
    return this.state !== 'recording' && this.state !== 'uploading';
  }

  get canStop(): boolean {
    return this.state === 'recording';
  }

  get canSend(): boolean {
    return !!this.recordedBlob && this.state !== 'recording' && this.state !== 'uploading';
  }

  get statusLabel(): string {
    switch (this.state) {
      case 'recording': return 'Recording...';
      case 'recorded': return 'Recording ready';
      case 'uploading': return 'Sending to transcription service...';
      case 'transcribed': return 'Transcription complete';
      case 'failed': return 'Action failed';
      default: return 'Ready';
    }
  }

  get formattedDuration(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60).toString().padStart(2, '0');
    const seconds = (this.elapsedSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  get fileSizeLabel(): string {
    if (!this.fileSize) return '-';
    if (this.fileSize < 1024 * 1024) return `${Math.round(this.fileSize / 1024)} KB`;
    return `${(this.fileSize / (1024 * 1024)).toFixed(2)} MB`;
  }

  async startRecording(): Promise<void> {
    if (!this.canRecord) return;

    this.clear(false);
    this.errorMessage = '';

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      this.mimeType = this.pickMimeType();
      this.recorder = new MediaRecorder(
        this.mediaStream,
        this.mimeType ? { mimeType: this.mimeType } : undefined
      );
      this.chunks = [];
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.state = 'recording';

      this.recorder.addEventListener('dataavailable', event => {
        if (event.data?.size) this.chunks.push(event.data);
      });

      this.recorder.addEventListener('stop', () => this.finalizeRecording());
      this.recorder.start(1000);
      this.timer = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000);
      }, 250);
    } catch (error) {
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : 'Microphone access failed.';
      this.stopTracks();
    }
  }

  stopRecording(): void {
    if (!this.recorder || this.recorder.state === 'inactive') return;
    this.recorder.stop();
  }

  async sendToTranscription(): Promise<void> {
    if (!this.recordedBlob) return;

    this.state = 'uploading';
    this.errorMessage = '';
    this.transcription = '';
    this.rawResponse = '';
    this.savedIntentResponse = '';
    this.showSavedIntent = false;

    const channelId = `rnd-${Date.now()}`;
    const form = new FormData();
    form.append('file', this.recordedBlob, `rnd-voice-${channelId}.${this.fileExtension()}`);
    form.append('channelId', channelId);
    form.append('sequenceNo', '1');
    form.append('speakerLabel', 'R&D');
    form.append('startMs', '0');
    form.append('language', environment.transcriptionLanguage || 'ur');

    try {
      const response = await fetch(`${environment.transcriptionWorkerUrl}/transcribe/chunk`, {
        method: 'POST',
        body: form,
      });

      const text = await response.text();
      if (!response.ok) {
        throw new Error(`Transcription worker returned HTTP ${response.status}: ${text || response.statusText}`);
      }

      const json = text ? JSON.parse(text) : {};
      this.rawResponse = JSON.stringify(json, null, 2);
      this.transcription = this.extractTranscription(json);
      await this.saveVoiceAnalysis();
      this.state = 'transcribed';
    } catch (error) {
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : 'Transcription failed.';
    }
  }

  async loadSavedRecords(): Promise<void> {
    this.recordsLoading = true;
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.rndApiBase}/voice-transcriptions?limit=50`)
      );
      this.savedRecords = response?.data?.records || response?.records || [];
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to load saved R&D records.';
    } finally {
      this.recordsLoading = false;
    }
  }

  async previewSavedAudio(record: SavedVoiceTranscription): Promise<void> {
    if (record.previewUrl) {
      URL.revokeObjectURL(record.previewUrl);
      record.previewUrl = '';
      return;
    }

    try {
      const blob = await firstValueFrom(
        this.http.get(`${this.rndApiBase}/voice-transcriptions/${record.id}/audio`, {
          responseType: 'blob',
        })
      );
      record.previewUrl = URL.createObjectURL(blob);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to load saved audio.';
    }
  }

  async deleteSavedRecord(record: SavedVoiceTranscription): Promise<void> {
    if (!record.id) return;

    try {
      await firstValueFrom(this.http.delete(`${this.rndApiBase}/voice-transcriptions/${record.id}`));
      if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
      this.savedRecords = this.savedRecords.filter(item => item.id !== record.id);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to delete saved row.';
    }
  }

  clear(resetState = true): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try { this.recorder.stop(); } catch {}
    }
    this.stopTimer();
    this.stopTracks();
    this.recorder = null;
    this.chunks = [];
    this.recordedBlob = null;
    this.fileSize = 0;
    this.mimeType = '';
    this.elapsedSeconds = 0;
    this.transcription = '';
    this.rawResponse = '';
    this.savedIntentResponse = '';
    this.errorMessage = '';
    this.showRaw = false;
    this.showSavedIntent = false;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = '';
    if (resetState) this.state = 'idle';
  }

  ngOnDestroy(): void {
    this.clear(false);
    this.savedRecords.forEach(record => {
      if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
    });
  }

  fileSizeFor(bytes?: number): string {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  durationFor(seconds?: number): string {
    if (seconds === undefined || seconds === null) return '-';
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const rest = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${rest}`;
  }

  confidenceFor(value?: number): string {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value * 100)}%`;
  }

  dateFor(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  private finalizeRecording(): void {
    this.stopTimer();
    this.stopTracks();

    this.recordedBlob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.fileSize = this.recordedBlob.size;
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    this.audioUrl = URL.createObjectURL(this.recordedBlob);
    this.state = this.recordedBlob.size ? 'recorded' : 'failed';
    if (!this.recordedBlob.size) this.errorMessage = 'No audio was captured.';
  }

  private pickMimeType(): string {
    return [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ].find(type => MediaRecorder.isTypeSupported(type)) || '';
  }

  private fileExtension(): string {
    if (this.mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  }

  private extractTranscription(response: any): string {
    const segments = Array.isArray(response?.segments) ? response.segments as TranscriptionSegment[] : [];
    const fromSegments = segments
      .map(segment => (segment.text || '').trim())
      .filter(Boolean)
      .join('\n');

    return fromSegments || response?.text || response?.transcription || 'No transcription text returned.';
  }

  private async saveVoiceAnalysis(): Promise<void> {
    if (!this.recordedBlob || !this.transcription || this.transcription === 'No transcription text returned.') {
      return;
    }

    this.savingAnalysis = true;
    const form = new FormData();
    form.append('file', this.recordedBlob, `rnd-voice-${Date.now()}.${this.fileExtension()}`);
    form.append('transcriptionText', this.transcription);
    form.append('transcriptionResponse', this.rawResponse);
    form.append('durationSeconds', String(this.elapsedSeconds));
    if (this.startedAt) {
      form.append('recordedAtEpochMs', String(this.startedAt));
    }

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.rndApiBase}/voice-transcriptions`, form)
      );
      const saved = response?.data || response;
      this.savedIntentResponse = this.prettyJson(saved?.intentResponse);
      await this.loadSavedRecords();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Intent analysis save failed.';
    } finally {
      this.savingAnalysis = false;
    }
  }

  private get rndApiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd`;
  }

  private prettyJson(value?: string): string {
    if (!value) return '';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  private stopTracks(): void {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
  }

  private stopTimer(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
